import { Color, Spacing } from '@persona-id/design-tokens';
import { MergeIcon, UsersCheckIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import PersonaIcon from 'dashboard/components/PersonaIcon';
import CaseIcon from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent/CaseIcon';
import ReportIcon from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent/ReportIcon';
import SARIcon from 'dashboard/components/WorkflowControlPanel/TriggerConfigEvent/SARIcon';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { Elevation } from 'design-system/variables/effect';
import { FontWeight } from 'design-system/variables/text';
import { EventType } from 'lib/constants/events';

const CARD_BORDER_WIDTH = 2;

const Card = styled('div')<{
  active?: boolean;
  width?: number;
}>`
  position: relative;
  display: flex;
  width: ${({ width }) => (width == null ? '256px' : '100%')};
  height: 68px;
  border: ${CARD_BORDER_WIDTH}px solid
    ${({ active }) => (active ? Color.CorePurple800 : Color.CoreSlate50)};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  box-shadow: ${Elevation.Mid};
  padding: 14px;
  background-color: ${Color.PrimaryWhite};
  box-sizing: border-box;
  cursor: pointer;
`;

const LOGO_WIDTH = 36;

const Logo = styled('div')`
  width: ${LOGO_WIDTH}px;
  height: ${LOGO_WIDTH}px;
  padding: 6px;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  background: ${Color.CorePurple100};
`;

const Text = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacing[3]};
  overflow: hidden;
`;

const Name = styled(Label)`
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// NOTE: should this be made into size xs for Paragraph?
const Type = styled(Paragraph)`
  font-size: 12px;
  font-weight: ${FontWeight.Regular};
  line-height: 14px;
  letter-spacing: unset;
`;

interface OptionCardProps {
  chooseEventType: (type: EventType) => void;
  type: EventType;
  active: boolean;
}

const OptionCard = ({ chooseEventType, type, active }: OptionCardProps) => {
  const [cardType, cardIcon] = (() => {
    switch (type) {
      case EventType.Account:
        return [
          'Account',
          <UsersCheckIcon
            size={24}
            color={active ? Color.CorePurple800 : Color.CorePurple600}
            key='account icon'
          />,
        ];
      case EventType.Case:
        return ['Case', <CaseIcon active={active} key='case icon' />];
      case EventType.CaseMessage:
        return ['Case Message', <CaseIcon active={active} key='case message icon' />];
      case EventType.Report:
        return ['Report', <ReportIcon active={active} key='report icon' />];
      case EventType.Inquiry:
        return ['Inquiry', <PersonaIcon active={active} key='inquiry icon' />];
      case EventType.SAR:
        return ['SAR', <SARIcon active={active} key='sar icon' />];
      case EventType.Transaction:
        return [
          'Transaction',
          <MergeIcon
            color={active ? Color.CorePurple800 : Color.CorePurple600}
            size={24}
            key='transaction icon'
          />,
        ];
      default:
        throw new Error('Invalid event type as trigger');
    }
  })();

  return (
    <Card
      id={`option-card__card-${type}`}
      role='button'
      aria-label={`${cardType} Status`}
      active={active}
      aria-pressed={active}
      width={256}
      onClick={() => chooseEventType(type)}
    >
      <Logo>{cardIcon}</Logo>
      <Text>
        <Name size='sm'>{cardType} Status</Name>
        <Spacer height={0.5} />
        <Type>Trigger based on the {cardType} event</Type>
      </Text>
    </Card>
  );
};

export default OptionCard;
